import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

import {
    BusinessClassification,
    BusinessPacket,
    BusinessParticipant,
    FidelitySmaBusinessOptions,
} from '@ndvr/customer-api';

import {
    FidelityAccountList,
    FidelityApplicationSection,
    FidelityCompletionRequirements,
    FidelityDownloadButton,
    FidelityDownloadFeedback,
    FidelityPacketCard,
} from '@/components/fidelity/FidelityApplicationComponents';
import { Button } from '@/components/ui/button';
import { CustomerApi } from 'API';

import { accountTitle } from './account-model';
import { FidelitySmaSectionProps } from './useFidelitySmaApplications';

const classifications: Record<BusinessClassification, string> = {
    CORPORATION_C: 'Corporation — C corporation',
    CORPORATION_S: 'Corporation — S corporation',
    LLC_C: 'LLC — C corporation',
    LLC_S: 'LLC — S corporation',
    LLC_PARTNERSHIP: 'LLC — partnership',
    LLC_SOLE_PROPRIETOR: 'LLC — sole proprietorship / disregarded entity',
    PARTNERSHIP: 'Partnership',
    UNINCORPORATED: 'Unincorporated business (not an LLC)',
    SOLE_PROPRIETOR: 'Sole proprietorship',
};
const controlClass = 'border-border rounded border bg-white p-2 text-sm';

export const FidelityBusinessApplicationView = ({
    household,
    applications,
    action,
    ssnAccess,
    checkingAccess,
}: FidelitySmaSectionProps<'business'>) => {
    const accountGUIDs = household.accounts.map(({ account }) => account.accountGUID);

    const title = (guid: string) => {
        const row = household.accounts.find(({ account }) => account.accountGUID === guid);
        return row ? accountTitle(row.account) : 'Account no longer available';
    };
    return (
        <FidelityApplicationSection
            name="Business"
            hasAccounts={accountGUIDs.length > 0}
            groupCount={applications.data?.applications.length}
            checkingAccess={checkingAccess}
            ssnAccess={!!ssnAccess}
            applications={applications}
        >
            <>
                {applications.data?.applications.map((packet) => (
                    <BusinessDraft key={JSON.stringify(packet)} packet={packet} title={title} action={action} />
                ))}
            </>
            {!!ssnAccess && accountGUIDs.length > 0 && (
                <Button variant="outline" onClick={() => void applications.refetch()}>
                    Refresh Business applications
                </Button>
            )}
        </FidelityApplicationSection>
    );
};

const BusinessDraft = ({
    packet,
    title,
    action,
}: {
    packet: BusinessPacket;
    title: (guid: string) => string;
    action: FidelitySmaSectionProps<'business'>['action'];
}) => {
    const [options, setOptions] = useState<FidelitySmaBusinessOptions>({ participants: [] });
    const generate = useMutation({
        mutationFn: () =>
            CustomerApi({ hideError: true }).getSmaBusinessForm(options, { accountGUIDs: packet.accountGUIDs }),
    });
    const update = (patch: Partial<FidelitySmaBusinessOptions>) => {
        generate.reset();
        setOptions((previous) => ({ ...previous, ...patch }));
    };
    const updateParty = (guid: string, patch: Partial<BusinessParticipant>) => {
        const previous = options.participants.find((party) => party.personOrgGUID === guid) ?? {
            personOrgGUID: guid,
            authorizedIndividual: false,
            controlPerson: false,
            entityOwner: false,
        };
        const changed = { ...previous, ...patch };
        update({
            participants: (options.participants.some((party) => party.personOrgGUID === guid)
                ? options.participants.map((party) => (party.personOrgGUID === guid ? changed : party))
                : [...options.participants, changed]
            ).filter((party) => party.authorizedIndividual || party.controlPerson || party.entityOwner),
        });
    };
    const separateTaxOwner =
        options.classification === BusinessClassification.LLC_SOLE_PROPRIETOR ||
        options.classification === BusinessClassification.SOLE_PROPRIETOR;
    return (
        <FidelityPacketCard name="Business" accountCount={packet.accountGUIDs.length}>
            <FidelityAccountList action={action} accountGUIDs={packet.accountGUIDs} title={title} />
            <fieldset disabled={generate.isPending} className="flex flex-col gap-4">
                <legend className="mb-3 font-medium">
                    Optional draft details — leave unknown information for manual completion
                </legend>
                <label className="flex flex-col gap-1">
                    Legal registration and federal tax classification
                    <select
                        className={controlClass}
                        value={options.classification ?? ''}
                        onChange={(event) =>
                            update({
                                classification: (event.target.value as BusinessClassification) || undefined,
                                taxOwnerGUID: undefined,
                            })
                        }
                    >
                        <option value="">Complete manually</option>
                        {Object.entries(classifications).map(([value, label]) => (
                            <option key={value} value={value}>
                                {label}
                            </option>
                        ))}
                    </select>
                </label>
                <label className="flex flex-col gap-1">
                    Business status (not applicable to sole proprietors)
                    <select
                        className={controlClass}
                        value={options.operating === undefined ? '' : String(options.operating)}
                        onChange={(event) =>
                            update({ operating: event.target.value === '' ? undefined : event.target.value === 'true' })
                        }
                    >
                        <option value="">Complete manually</option>
                        <option value="true">Operating</option>
                        <option value="false">Non-operating</option>
                    </select>
                </label>
                <label className="flex flex-col gap-1">
                    Is this business a broker-dealer?
                    <select
                        className={controlClass}
                        value={options.brokerDealer === undefined ? '' : String(options.brokerDealer)}
                        onChange={(event) =>
                            update({
                                brokerDealer: event.target.value === '' ? undefined : event.target.value === 'true',
                            })
                        }
                    >
                        <option value="">Confirm manually</option>
                        <option value="false">No</option>
                        <option value="true">Yes — this application cannot be used</option>
                    </select>
                </label>
                {separateTaxOwner && (
                    <label className="flex flex-col gap-1">
                        Tax owner (verify the tax owner’s identity and identifier)
                        <select
                            className={controlClass}
                            value={options.taxOwnerGUID ?? ''}
                            onChange={(event) => update({ taxOwnerGUID: event.target.value || undefined })}
                        >
                            <option value="">Complete name and tax ID manually</option>
                            {packet.linkedParties.map((party) => (
                                <option key={party.personOrgGUID} value={party.personOrgGUID}>
                                    {party.name}
                                </option>
                            ))}
                        </select>
                    </label>
                )}
                <p className="text-cocoa-600 text-sm">
                    Select confirmed roles: up to four authorized people and one additional control person/owner.
                    Certifying officer first; include all managing partners.
                </p>
                {packet.linkedParties.map((party) => {
                    const selected = options.participants.find((entry) => entry.personOrgGUID === party.personOrgGUID);
                    return (
                        <div key={party.personOrgGUID} className="border-border rounded border p-3">
                            <p className="font-medium">{party.name}</p>
                            <p className="text-cocoa-600 text-sm">Saved: {party.savedRoles}</p>
                            {party.selectable ? (
                                <div className="mt-2 flex flex-wrap gap-3">
                                    {(
                                        [
                                            ['authorizedIndividual', 'Authorized individual'],
                                            ['controlPerson', 'Control person'],
                                            ['entityOwner', 'Qualifying entity owner'],
                                        ] as const
                                    ).map(([key, label]) => (
                                        <label key={key} className="flex items-center gap-2">
                                            <input
                                                type="checkbox"
                                                checked={selected?.[key] ?? false}
                                                onChange={(event) =>
                                                    updateParty(party.personOrgGUID, { [key]: event.target.checked })
                                                }
                                            />
                                            {label}
                                        </label>
                                    ))}
                                    {!!selected && (
                                        <label className="flex items-center gap-2">
                                            Business title
                                            <input
                                                className={controlClass}
                                                value={selected.businessTitle ?? ''}
                                                onChange={(event) =>
                                                    updateParty(party.personOrgGUID, {
                                                        businessTitle: event.target.value,
                                                    })
                                                }
                                            />
                                        </label>
                                    )}
                                </div>
                            ) : (
                                <p className="mt-2 text-sm">Manual completion required.</p>
                            )}
                        </div>
                    );
                })}
                {!!options.participants.length && (
                    <ol className="list-inside list-decimal text-sm" aria-label="Selected participant order">
                        {options.participants.map((party) => (
                            <li key={party.personOrgGUID}>
                                {
                                    packet.linkedParties.find((linked) => linked.personOrgGUID === party.personOrgGUID)
                                        ?.name
                                }
                                {party.authorizedIndividual
                                    ? ' — authorized-person page and signature name'
                                    : ' — beneficial-ownership page only'}
                            </li>
                        ))}
                    </ol>
                )}
            </fieldset>
            <FidelityCompletionRequirements requirements={packet.completionRequirements} beforeChoices />
            <FidelityDownloadButton
                download={generate}
                variant="outline"
                disabled={options.brokerDealer === true}
                onClick={() => generate.mutate()}
            >
                Generate SMA Business Application
            </FidelityDownloadButton>
            <FidelityDownloadFeedback download={generate} />
        </FidelityPacketCard>
    );
};
